import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import ProcessSection from "@/sections/ProcessSection";
import CTASection from "@/components/CTASection";
import { LinkButton, ActionButton } from "@/components/Button";
import {
  STAFFING_CAPABILITIES,
  ENGAGEMENT_MODELS,
  WHY_STAFFING,
  STAFFING_PROCESS,
  TECH_AREAS,
  STAFFING_AUDIENCES,
  TEAM_CHIPS,
} from "@/lib/staffing";
import { Check, Plus, ArrowDown } from "lucide-react";

const TeamVisual = () => (
  <div className="relative mx-auto w-full max-w-xl">
    <div className="glass-card relative rounded-2xl p-6 sm:p-8" data-testid="staffing-team-visual">
      {/* Sources */}
      <div className="flex items-center justify-center gap-3">
        <span className="rounded-full bg-kryvo-green px-4 py-2 text-xs font-semibold text-white">Kryvo</span>
        <Plus className="h-4 w-4 text-kryvo-green" aria-hidden="true" />
        <span className="rounded-full border border-kryvo-line bg-white px-4 py-2 text-xs font-semibold text-kryvo-ink">
          Your Business
        </span>
      </div>
      <div className="my-4 flex justify-center">
        <ArrowDown className="h-4 w-4 text-kryvo-green/50" aria-hidden="true" />
      </div>

      {/* Hub */}
      <div className="rounded-xl border border-kryvo-green/25 bg-kryvo-soft/60 p-5 text-center">
        <p className="font-display text-base font-semibold text-kryvo-deep">Extended Technology Team</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {TEAM_CHIPS.map((chip) => (
            <span
              key={chip}
              className="rounded-full border border-kryvo-line bg-white px-3 py-1.5 text-[11px] font-medium text-kryvo-ink/75"
            >
              {chip}
            </span>
          ))}
        </div>
      </div>

      <div className="my-4 flex justify-center">
        <ArrowDown className="h-4 w-4 text-kryvo-green/50" aria-hidden="true" />
      </div>

      {/* Outcome */}
      <div className="flex items-center justify-center gap-2 rounded-xl bg-kryvo-green px-4 py-3 text-center">
        <span className="text-xs font-semibold text-white">Product Development → Delivery &amp; Growth</span>
      </div>
    </div>

    <div className="animate-float absolute -right-3 -top-4 hidden md:block">
      <div className="glass-card rounded-xl px-4 py-2.5 text-xs font-semibold text-kryvo-deep">Dedicated Team</div>
    </div>
    <div className="animate-float-slow absolute -bottom-4 -left-3 hidden md:block">
      <div className="glass-card rounded-xl px-4 py-2.5 text-xs font-semibold text-kryvo-deep">Scale On Demand</div>
    </div>
  </div>
);

const Staffing = () => {
  const scrollToServices = () =>
    document.getElementById("staffing-services")?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <Seo
        title="Offshore IT Staffing Services | Kryvo Technologies"
        description="Kryvo Technologies provides offshore IT staffing and dedicated technology resources to help businesses extend their engineering teams and scale digital product development."
      />

      {/* Hero */}
      <section
        className="grid-bg relative overflow-hidden bg-gradient-to-b from-white via-kryvo-mist/60 to-kryvo-mist pb-16 pt-[calc(72px+3rem)] sm:pb-20 lg:pt-[calc(72px+4rem)]"
        data-testid="staffing-hero"
      >
        <div
          className="pointer-events-none absolute -right-40 -top-40 h-[420px] w-[420px] rounded-full bg-kryvo-lime/25 blur-3xl"
          aria-hidden="true"
        />
        <div className="container-x relative grid items-center gap-14 lg:grid-cols-2 lg:gap-10">
          <Reveal className="max-w-xl">
            <span className="eyebrow" data-testid="staffing-eyebrow">
              <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
              IT Staffing
            </span>
            <h1 className="mt-4 text-balance text-4xl font-semibold leading-[1.08] text-kryvo-ink sm:text-5xl">
              Build Your Team. <span className="text-kryvo-green">Scale Your Technology.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-kryvo-sub sm:text-lg">
              Access skilled technology professionals and dedicated offshore teams to extend your
              engineering capabilities, accelerate delivery and scale with confidence.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <LinkButton to="/contact" variant="primary" testId="staffing-hero-cta">
                Let's Talk
              </LinkButton>
              <ActionButton variant="outline" arrow={false} testId="staffing-hero-secondary" onClick={scrollToServices}>
                Explore Staffing Services
              </ActionButton>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <TeamVisual />
          </Reveal>
        </div>
      </section>

      {/* Offshore Staffing Services */}
      <section id="staffing-services" className="container-x scroll-mt-24 py-16 sm:py-24" data-testid="staffing-services">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
          <SectionHeader
            align="left"
            eyebrow="Staffing Services"
            title="Offshore Staffing Services"
            description="Extend your technology team with skilled professionals who work as an integrated part of your organization."
          />
          <Reveal>
            <div className="flex flex-col gap-4">
              <p className="text-base leading-relaxed text-kryvo-sub">
                Kryvo supports businesses that need additional technology capacity — without having to
                build the entire team internally. Our offshore staffing model gives you access to
                technology professionals who join your workflows, your tools and your goals.
              </p>
              <ul className="mt-2 flex flex-col gap-3">
                {[
                  "Add engineering capacity without long hiring cycles",
                  "Professionals who integrate with your existing team and processes",
                  "Flexible engagement — from a single specialist to a full product team",
                  "Aligned with digital product development, not generic body-shopping",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm font-medium text-kryvo-ink">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-kryvo-green" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="border-y border-kryvo-line bg-kryvo-mist py-16 sm:py-24" data-testid="staffing-capabilities">
        <div className="container-x">
          <SectionHeader
            eyebrow="Capabilities"
            title="Technology Professionals Across Key Roles"
            description="Technology professionals across key roles including development, quality, design and cloud — matched to your project's requirements."
          />
          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-5">
            {STAFFING_CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.04}>
                <div className="group flex h-full flex-col items-center gap-3 rounded-2xl border border-kryvo-line bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-kryvo-green/40 hover:shadow-[0_18px_40px_-20px_rgba(8,122,61,0.25)]">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green transition-colors duration-300 group-hover:bg-kryvo-green group-hover:text-white">
                    <c.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="text-sm font-semibold text-kryvo-ink">{c.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="container-x py-16 sm:py-24" data-testid="staffing-engagement-models">
        <SectionHeader
          eyebrow="Engagement Models"
          title="Flexible Engagement Models"
          description="Choose the model that fits your product, your timeline and your team."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ENGAGEMENT_MODELS.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.07}>
              <div className="h-full rounded-2xl border border-kryvo-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-kryvo-green/40 hover:shadow-[0_20px_45px_-22px_rgba(8,122,61,0.25)]">
                <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green">
                  <m.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="text-lg font-semibold text-kryvo-ink">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-kryvo-sub">{m.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why Kryvo Staffing */}
      <section className="border-y border-kryvo-line bg-kryvo-mist py-16 sm:py-24" data-testid="staffing-why">
        <div className="container-x">
          <SectionHeader
            eyebrow="Why Kryvo"
            title="Why Work With Kryvo"
            description="Staffing from a technology company — built around delivery, not headcount."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_STAFFING.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="flex h-full items-start gap-4 rounded-2xl border border-kryvo-line bg-white p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green">
                    <w.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-semibold text-kryvo-ink">{w.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-kryvo-sub">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <ProcessSection
        eyebrow="Our Process"
        title="How It Works"
        description="A simple, transparent path from requirement to a working extension of your team."
        steps={STAFFING_PROCESS}
        testId="staffing-process"
      />

      {/* Technology Expertise */}
      <section className="border-y border-kryvo-line bg-kryvo-mist py-16 sm:py-24" data-testid="staffing-tech-areas">
        <div className="container-x">
          <SectionHeader
            eyebrow="Technology Expertise"
            title="Broad, Product-Focused Expertise"
            description="Staffing across the technology areas modern digital products are built on."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TECH_AREAS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06}>
                <div className="group h-full rounded-2xl border border-kryvo-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-kryvo-green/40 hover:shadow-[0_20px_45px_-22px_rgba(8,122,61,0.25)]">
                  <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-kryvo-soft text-kryvo-green transition-colors duration-300 group-hover:bg-kryvo-green group-hover:text-white">
                    <t.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <h3 className="text-lg font-semibold text-kryvo-ink">{t.title}</h3>
                  <p className="mt-2 text-sm font-medium text-kryvo-green">{t.tags}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Support */}
      <section className="container-x py-16 sm:py-24" data-testid="staffing-audiences">
        <SectionHeader
          eyebrow="Who We Support"
          title="Built Around Your Team"
          description="Offshore staffing that adapts to your stage — whatever size your business is."
        />
        <div className="mx-auto mt-12 flex max-w-4xl flex-wrap justify-center gap-3">
          {STAFFING_AUDIENCES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.06}>
              <span className="inline-flex items-center gap-2.5 rounded-full border border-kryvo-line bg-white px-5 py-3 text-sm font-semibold text-kryvo-ink transition-colors duration-300 hover:border-kryvo-green/40 hover:bg-kryvo-mist">
                <a.icon className="h-4 w-4 text-kryvo-green" aria-hidden="true" />
                {a.title}
              </span>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection
        title="Need to Extend Your Technology Team?"
        description="Tell us what you need and we'll help you explore the right staffing model for your business."
        cta="Talk to Our Team"
      />
    </>
  );
};

export default Staffing;
